import React, { useEffect, useState } from 'react';
import { AdminGuard } from './AdminPages';
import AdminShell from './AdminShell';
import { SectionTitle } from './PageShell';
import { api } from '../lib/api';
import { Loader2, BarChart3, Eye, TrendingUp, CalendarDays, Car, Bike } from 'lucide-react';

const PERIODS = [
  { days: 7, label: '7 jours' },
  { days: 30, label: '30 jours' },
  { days: 90, label: '90 jours' },
];

const PAGE_LABELS = {
  '/': 'Accueil',
  '/eleve': 'Espace élève',
  '/enseignant': 'Espace enseignant',
  '/magazine': 'Magazine',
  '/contact': 'Contact',
  '/livret': 'Livret',
  '/tutos': 'Tutos vidéo',
  '/faq': 'FAQ',
  '/shorts': 'Shorts',
  '/shorts/proposer': 'Proposer un short',
  '/videos': 'Vidéos communauté',
  '/annuaire': 'Annuaire écoles',
  '/cours-interactifs': 'Cours interactifs',
  '/moto': 'Accueil Moto',
  '/livret-am/lecteur': 'Livret AM',
  '/cours/progression': 'Ma progression',
};

export default function AdminStatsPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.adminPageStats(days).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [days]);

  const maxPage = Math.max(1, ...(data?.top_pages || []).map((p) => p.views));
  const maxDay = Math.max(1, ...(data?.per_day || []).map((d) => d.views));

  return (
    <AdminGuard>
      <AdminShell>
        <div data-testid="admin-stats-page">
          <SectionTitle
            kicker="Back-office · Audience"
            title="Statistiques des pages"
            sub="Fréquentation du site : pages les plus consultées et évolution jour par jour."
          />
          <div className="flex items-center gap-2 mb-8">
            {PERIODS.map((p) => (
              <button
                key={p.days}
                onClick={() => setDays(p.days)}
                data-testid={`stats-period-${p.days}`}
                className={`h-9 px-4 rounded-full text-[12.5px] font-semibold transition ${days === p.days ? 'bg-red-600 text-white' : 'border border-white/10 text-white/60 hover:text-white hover:bg-white/5'}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-white/50 inline-flex items-center gap-2 py-10"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
          ) : !data ? (
            <div className="text-white/50 py-10">Impossible de charger les statistiques.</div>
          ) : (
            <>
              <div className="grid sm:grid-cols-3 gap-4 mb-8">
                <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5">
                  <div className="flex items-center gap-2 text-white/45 text-[10.5px] uppercase tracking-[0.22em] font-bold font-mono-spec mb-3"><Eye size={13} /> Vues totales</div>
                  <div className="font-serif-editorial text-white text-[42px] leading-none" data-testid="stats-total-views">{data.total}</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5">
                  <div className="flex items-center gap-2 text-white/45 text-[10.5px] uppercase tracking-[0.22em] font-bold font-mono-spec mb-3"><TrendingUp size={13} /> Moyenne / jour</div>
                  <div className="font-serif-editorial text-white text-[42px] leading-none">{data.per_day.length ? Math.round(data.total / Math.max(1, days)) : 0}</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5">
                  <div className="flex items-center gap-2 text-white/45 text-[10.5px] uppercase tracking-[0.22em] font-bold font-mono-spec mb-3"><BarChart3 size={13} /> Pages distinctes</div>
                  <div className="font-serif-editorial text-white text-[42px] leading-none">{data.top_pages.length}</div>
                </div>
              </div>

              {/* Évolution par jour */}
              <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 lg:p-6 mb-8">
                <div className="flex items-center gap-2 mb-5"><CalendarDays size={15} className="text-red-400" /><h3 className="font-serif-editorial text-white text-[20px]">Évolution jour par jour</h3></div>
                {data.per_day.length === 0 ? (
                  <div className="text-white/40 text-[13px] py-6">Pas encore de données — les visites sont comptées à partir de maintenant.</div>
                ) : (
                  <div className="flex items-end gap-[3px] h-36" data-testid="stats-day-chart">
                    {data.per_day.map((d) => (
                      <div key={d.day} className="flex-1 group relative flex flex-col justify-end h-full" style={{ maxWidth: 42 }}>
                        <div className="bg-red-600/70 hover:bg-red-500 rounded-t transition-all" style={{ height: `${Math.max(3, (d.views / maxDay) * 100)}%` }} />
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-black border border-white/15 rounded px-2 py-1 text-white text-[10.5px] whitespace-nowrap z-10">{d.day} · {d.views} vue{d.views > 1 ? 's' : ''}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Univers Voiture vs Moto */}
              {data.univers && (
                <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 lg:p-6 mb-8" data-testid="stats-univers">
                  <h3 className="font-serif-editorial text-white text-[20px] mb-5">Univers Voiture vs Moto</h3>
                  {(() => {
                    const v = data.univers.voiture || 0;
                    const m = data.univers.moto || 0;
                    const tot = Math.max(1, v + m);
                    return (
                      <>
                        <div className="flex h-4 rounded-full overflow-hidden mb-2">
                          <div className="bg-red-600" style={{ width: `${(v / tot) * 100}%` }} />
                          <div className="bg-sky-500" style={{ width: `${(m / tot) * 100}%` }} />
                        </div>
                        <div className="flex items-center justify-between text-[12px] font-mono-spec mb-6">
                          <span className="text-red-300 inline-flex items-center gap-1.5"><Car size={13} /> Voiture · {v} vues ({Math.round((v / tot) * 100)}%)</span>
                          <span className="text-sky-300 inline-flex items-center gap-1.5"><Bike size={13} /> Moto · {m} vues ({Math.round((m / tot) * 100)}%)</span>
                        </div>
                      </>
                    );
                  })()}
                  <div className="grid md:grid-cols-2 gap-6">
                    {[
                      { label: 'Top pages Voiture', icon: Car, list: data.top_voiture || [], color: 'bg-red-600', tid: 'stats-top-voiture' },
                      { label: 'Top pages Moto', icon: Bike, list: data.top_moto || [], color: 'bg-sky-500', tid: 'stats-top-moto' },
                    ].map(({ label, icon: Icon, list, color, tid }) => {
                      const mx = Math.max(1, ...list.map((p) => p.views));
                      return (
                        <div key={label} data-testid={tid}>
                          <div className="flex items-center gap-2 text-white/60 text-[11px] uppercase tracking-[0.18em] font-bold font-mono-spec mb-3"><Icon size={13} /> {label}</div>
                          {list.length === 0 ? (
                            <div className="text-white/35 text-[12.5px]">Aucune visite sur cette période.</div>
                          ) : (
                            <ul className="space-y-2.5">
                              {list.slice(0, 5).map((p) => (
                                <li key={p.path}>
                                  <div className="flex items-baseline justify-between gap-3">
                                    <span className="text-white/85 text-[12.5px] font-semibold truncate">{PAGE_LABELS[p.path] || p.path}</span>
                                    <span className="text-white/50 text-[11.5px] font-mono-spec shrink-0">{p.views}</span>
                                  </div>
                                  <div className="mt-1 h-1 rounded-full bg-white/5 overflow-hidden">
                                    <div className={`h-full ${color} rounded-full`} style={{ width: `${(p.views / mx) * 100}%` }} />
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Top pages */}
              <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 lg:p-6">
                <h3 className="font-serif-editorial text-white text-[20px] mb-5">Pages les plus consultées</h3>
                {data.top_pages.length === 0 ? (
                  <div className="text-white/40 text-[13px] py-6">Aucune donnée pour cette période.</div>
                ) : (
                  <ul className="space-y-3" data-testid="stats-top-pages">
                    {data.top_pages.map((p, i) => (
                      <li key={p.path} className="flex items-center gap-3">
                        <span className="w-6 text-white/35 text-[12px] font-mono-spec text-right">{i + 1}.</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="text-white text-[13.5px] font-semibold truncate">{PAGE_LABELS[p.path] || p.path}</span>
                            <span className="text-white/55 text-[12.5px] font-mono-spec shrink-0">{p.views} vue{p.views > 1 ? 's' : ''}</span>
                          </div>
                          {PAGE_LABELS[p.path] && <div className="text-white/35 text-[11px] font-mono-spec truncate">{p.path}</div>}
                          <div className="mt-1.5 h-1.5 rounded-full bg-white/5 overflow-hidden">
                            <div className="h-full bg-red-600 rounded-full" style={{ width: `${(p.views / maxPage) * 100}%` }} />
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </AdminShell>
    </AdminGuard>
  );
}
